/**
 * PDF 출력 페이지(PrintPage / PrintPage2 / RentPrintPage / RentPrintPage2)가
 * 서버 Puppeteer 에게 준비 상태를 알리는 계약.
 *
 * 배경: 백엔드는 `__VUE_PRINT_STATUS__` 가 'ready' | 'failed' 가 되기를 기다린다
 * (routes/index.js 의 page.waitForFunction). 'failed' 를 받으면 즉시 원인을 담아
 * 5xx 로 끝내지만, 종전에는 출력 페이지가 'failed' 를 거의 세팅하지 않았다. 그래서
 * init 이 어디선가 멈추면 대기 한도(당시 30초)를 통째로 버린 뒤 "PDF 생성 타임아웃" 만
 * 남고 **원인은 어디에도 기록되지 않았다** — 운영 로그의 타임아웃 29건이 전부 그 상태였다.
 *
 * 그래서 두 가지를 한다.
 *   1) 진행 단계 표시(markStage) — 어디까지 갔는지 남긴다. 백엔드가 대기 한도를 넘겨
 *      포기할 때 이 값을 읽어 "마지막 단계" 를 로그와 사용자 메시지에 담는다.
 *   2) 확실한 실패는 즉시 알린다(markFailed) — 예: 조회 결과가 없는 물건. 이건 더 기다려도
 *      달라지지 않으므로 한도까지 버티지 않고 바로 원인과 함께 끝낸다.
 *
 * **시간으로 실패를 단정하지 않는다.** 처음에는 한도보다 먼저 깨어나 "아직 준비가 안 됐으면
 * 실패" 로 처리하는 워치독을 뒀는데, 실측에서 조회가 32초 걸린 렌더가 35초 워치독에 걸려
 * 죽었다 — 그때 단계는 계속 진행 중이었다(item-loaded → dom-rendered). 느린 것과 멈춘 것은
 * 시간만으로 구분할 수 없고, 잘못 단정하면 원래 성공했을 출력을 빼앗는다. 그래서 판정은
 * 백엔드의 대기 한도 한 곳에만 두고, 이 모듈은 "어디까지 갔는지" 를 남기는 역할만 한다.
 *
 * 범위 한계(알고 두는 것): 이 상태는 window 전역 하나뿐이라, 일반 브라우저에서 출력 화면
 * 사이를 SPA 로 이동하면 앞 페이지의 늦은 비동기 콜백이 뒤 페이지의 상태를 건드릴 수 있다.
 * 실제 PDF 렌더는 headless 가 매번 새 페이지를 열어(window 가 비어 있음) 이 경로를 타지
 * 않고, 일반 브라우저에서는 이 값을 읽는 주체가 없다. 그래서 호출부마다 토큰을 들고 다니게
 * 만드는 대신 이 한계를 적어 둔다.
 */

/**
 * 서버(routes/index.js)가 콘솔 이벤트로 단계를 알아채는 접두사.
 * 양쪽이 반드시 같아야 하므로 값을 바꿀 때는 routes/index.js 도 함께 바꾼다.
 */
const STAGE_LOG_PREFIX = '[print-stage] ';

/**
 * 진행 단계를 남긴다. window 전역에 넣는 동시에 **콘솔로도 알린다** — 서버는 콘솔
 * 이벤트를 미리 모아 두었다가 대기 한도를 넘길 때 그 값을 원인으로 쓴다.
 * 전역만 두고 서버가 나중에 읽게 하면, 렌더가 멎은 경우(실패 유형 중 가장 흔하다)
 * 페이지에 말을 거는 것 자체가 불가능해 진단을 못 얻고 응답만 크게 늦어진다.
 * 콘솔 신호는 멎기 전에 이미 서버에 도착해 있으므로 그 경우에도 단계가 남는다.
 */
export function markStage(stage) {
	if (typeof window === 'undefined') return;
	window.__VUE_PRINT_STAGE__ = stage;
	try {
		console.log(STAGE_LOG_PREFIX + stage);
	} catch (e) { /* 콘솔이 없는 환경에서도 렌더는 계속되어야 한다 */ }
}

/**
 * 출력 페이지 mounted 초입에서 호출 — 이전 페이지의 결론을 지우고 단계를 초기화한다.
 * headless 렌더는 매번 새로 페이지를 열어 window 가 비어 있지만, 일반 브라우저에서 출력
 * 화면끼리 이동하면 앞 페이지의 'ready' 가 남는다. 그 상태로 두면 markFailed 가 "이미
 * ready" 로 보고 무시해(터미널 계약) 다음 페이지의 실패를 못 알린다.
 */
export function startPrintPage() {
	if (typeof window === 'undefined') return;
	window.__VUE_PRINT_STATUS__ = undefined;
	window.__VUE_READY__ = false;
	window.__VUE_FAIL_REASON__ = undefined;
	markStage('mounted');
}

/** 준비 완료 — 서버가 캡처를 시작해도 되는 시점에 호출. */
export function markReady() {
	if (typeof window === 'undefined') return;
	// 이미 'failed' 를 보냈다면 뒤집지 않는다. 백엔드의 waitForFunction 은 상태가 한 번
	// 정해지면 바뀌지 않는다고 보고(단조 계약), predicate 가 깨어난 뒤 다시 값을 읽는다.
	// 그 사이에 failed → ready 로 바꾸면 백엔드가 "준비됨" 으로 오해해 아직 덜 그려진
	// 페이지를 캡처한다 — 빈 PDF 보다 나쁜, 조용히 잘못된 PDF 가 된다.
	if (window.__VUE_PRINT_STATUS__ === 'failed') return;
	markStage('ready');
	window.__VUE_READY__ = true;          // 구 백엔드 호환
	window.__VUE_PRINT_STATUS__ = 'ready';
}

/**
 * 더 기다려도 달라지지 않는 실패 — 서버가 한도까지 버티지 않고 원인과 함께 끝내게 한다.
 * "느려서 아직 안 됐다" 에는 쓰지 않는다(그건 백엔드 한도가 판단한다).
 */
export function markFailed(reason) {
	if (typeof window === 'undefined') return;
	// 이미 ready 를 보냈다면 뒤집지 않는다(계약상 terminal).
	if (window.__VUE_PRINT_STATUS__ === 'ready' || window.__VUE_READY__ === true) return;
	// 이미 실패했다면 첫 사유를 지키고 덮어쓰지 않는다. 먼저 난 실패가 근본 원인이고,
	// 뒤따르는 실패는 대개 그 여파다.
	if (window.__VUE_PRINT_STATUS__ === 'failed') return;
	window.__VUE_FAIL_REASON__ = String(reason || 'unknown');
	window.__VUE_PRINT_STATUS__ = 'failed';
}
